/**
 * Date filter utilities for SMSWiser drops.
 * SMSWiser uses "DD/MM/YYYY HH:mm" for startedAt/executeAfter/finishedAt.
 * Date-only comparison (YYYY-MM-DD) keeps the logic timezone-stable.
 * For Media Devoted | Created By Ciro Autiero
 */

/** Minimal drop shape needed to derive a comparison date. */
export interface SmswiserDropDateLike {
  finishedAt?: string | null;
  startedAt?: string | null;
  status?: { startedAt?: string | null } | null;
  executeAfter?: string | null;
}

/**
 * Parse SMSWiser date "DD/MM/YYYY HH:mm" (or "DD/MM/YYYY") to YYYY-MM-DD.
 * Returns null if the value is empty or unparseable.
 */
export function parseSmswiserDateToYmd(
  value: string | null | undefined,
): string | null {
  const s = (value ?? '').trim();
  if (!s) return null;
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (!m) return null;
  const [, dd, mm, yyyy] = m;
  const day = String(Number(dd)).padStart(2, '0');
  const month = String(Number(mm)).padStart(2, '0');
  return `${yyyy}-${month}-${day}`;
}

/** Extract YYYY-MM-DD from an ISO timestamp (e.g. "2026-01-17T…" → "2026-01-17"). */
export function isoToYmd(iso: string | null | undefined): string | null {
  if (!iso || typeof iso !== 'string') return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? m[0] : null;
}

/**
 * Pick the calendar day used for date-range filtering and pagination stop.
 * Matches SMSWiser "Start" column: startedAt → executeAfter → finishedAt.
 */
export function getDropDateYmd(drop: SmswiserDropDateLike): string | null {
  const startedAt = drop.startedAt ?? drop.status?.startedAt ?? null;
  return (
    parseSmswiserDateToYmd(startedAt) ??
    parseSmswiserDateToYmd(drop.executeAfter) ??
    parseSmswiserDateToYmd(drop.finishedAt)
  );
}

/**
 * True when the drop's effective date is within [from, to] (inclusive, date-only).
 * Drops without any parseable date are excluded.
 * Invalid range bounds short-circuit to "include" so callers can rely on filter inputs.
 */
export function dropInDateRange(
  drop: SmswiserDropDateLike,
  from: string,
  to: string,
): boolean {
  const dropYmd = getDropDateYmd(drop);
  if (!dropYmd) return false;
  const fromYmd = isoToYmd(from);
  const toYmd = isoToYmd(to);
  if (!fromYmd || !toYmd) return true;
  return dropYmd >= fromYmd && dropYmd <= toYmd;
}

/**
 * Stop paginating once the oldest drop in the batch is strictly before `fromYmd`.
 * Uses the same Start-column date as {@link getDropDateYmd}.
 */
export function shouldStopPaginating(params: {
  batch: SmswiserDropDateLike[];
  fromYmd: string;
}): boolean {
  if (!params.batch.length) return true;
  let oldest: string | null = null;
  for (const drop of params.batch) {
    const ymd = getDropDateYmd(drop);
    if (!ymd) continue;
    if (!oldest || ymd < oldest) oldest = ymd;
  }
  return oldest !== null && oldest < params.fromYmd;
}
